/**
 * useBatchEmailSend Hook
 *
 * Handles batch email sending via the /api/hmc/email/batch-send endpoint.
 * Supports both individual (personalized) and bulk BCC modes.
 */

import { useState, useCallback } from 'react';
import type { EPGContactWithOrg } from './useEpgContactFilter';

// Get primary email from contact
function getContactPrimaryEmail(contact: EPGContactWithOrg): string | null {
  return contact.email_work || contact.email_home || contact.email_other || null;
}

export interface BatchEmailRecipient {
  email: string;
  first_name: string | null;
  organization_name: string | null;
}

export interface BatchEmailResult {
  email: string;
  success: boolean;
  error?: string;
}

export interface BatchEmailResponse {
  total: number;
  success_count: number;
  failed_count: number;
  results: BatchEmailResult[];
}

export interface SendProgress {
  status: 'idle' | 'sending' | 'complete' | 'error';
  sent: number;
  total: number;
  errors: Array<{ email: string; error: string }>;
}

export interface UseBatchEmailSendResult {
  progress: SendProgress;
  sendEmails: (params: SendEmailsParams) => Promise<void>;
  resetProgress: () => void;
}

export interface SendEmailsParams {
  contacts: EPGContactWithOrg[];
  subject: string;
  htmlBody: string;
  sendMode: 'individual' | 'bulk-bcc';
  bccToEmail?: string;
  greetingTemplate?: string;
  fallbackGreeting?: string;
  delayMs?: number;
}

const initialProgress: SendProgress = {
  status: 'idle',
  sent: 0,
  total: 0,
  errors: [],
};

/**
 * Hook for sending batch emails
 */
export function useBatchEmailSend(): UseBatchEmailSendResult {
  const [progress, setProgress] = useState<SendProgress>(initialProgress);

  const resetProgress = useCallback(() => {
    setProgress(initialProgress);
  }, []);

  const sendEmails = useCallback(async (params: SendEmailsParams) => {
    const {
      contacts,
      subject,
      htmlBody,
      sendMode,
      bccToEmail,
      greetingTemplate = 'Hi {first_name},',
      fallbackGreeting = 'Hi there,',
      delayMs = 500,
    } = params;

    // Validate
    if (contacts.length === 0) {
      setProgress({
        status: 'error',
        sent: 0,
        total: 0,
        errors: [{ email: '', error: 'No contacts selected' }],
      });
      return;
    }

    if (sendMode === 'bulk-bcc' && !bccToEmail) {
      setProgress({
        status: 'error',
        sent: 0,
        total: 0,
        errors: [{ email: '', error: 'BCC To email is required for bulk mode' }],
      });
      return;
    }

    // Prepare recipients
    const recipients: BatchEmailRecipient[] = contacts
      .map((contact) => ({
        email: getContactPrimaryEmail(contact) || '',
        first_name: String(contact.first_name || '') || null,
        organization_name: contact.organization_name || null,
      }))
      .filter((r) => r.email); // Filter out contacts without email

    if (recipients.length === 0) {
      setProgress({
        status: 'error',
        sent: 0,
        total: 0,
        errors: [{ email: '', error: 'No contacts with email addresses' }],
      });
      return;
    }

    // Start sending
    setProgress({
      status: 'sending',
      sent: 0,
      total: recipients.length,
      errors: [],
    });

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/hmc/email/batch-send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          recipients,
          subject,
          html_body: htmlBody,
          send_mode: sendMode === 'bulk-bcc' ? 'bulk_bcc' : 'individual',
          bcc_to_email: sendMode === 'bulk-bcc' ? bccToEmail : undefined,
          delay_ms: delayMs,
          greeting_template: greetingTemplate,
          fallback_greeting: fallbackGreeting,
        }),
      });

      const data: BatchEmailResponse = await response.json();

      if (!response.ok) {
        throw new Error((data as unknown as { detail?: string }).detail || 'Failed to send emails');
      }

      // Collect errors
      const errors = data.results
        .filter((r) => !r.success)
        .map((r) => ({ email: r.email, error: r.error || 'Unknown error' }));

      setProgress({
        status: data.failed_count === data.total ? 'error' : 'complete',
        sent: data.success_count,
        total: data.total,
        errors,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Network error';
      setProgress({
        status: 'error',
        sent: 0,
        total: recipients.length,
        errors: [{ email: '', error: errorMessage }],
      });
    }
  }, []);

  return {
    progress,
    sendEmails,
    resetProgress,
  };
}

export default useBatchEmailSend;
